// Cookie management for authentication tokens
import axios from 'axios';

// Session duration: 1 day for access token, 30 days for refresh token
const ACCESS_TOKEN_MAX_AGE = 24 * 60 * 60; // 1 day in seconds
const REFRESH_TOKEN_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

// Track if a refresh is already in progress to avoid duplicate requests
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export function setAuthCookies(accessToken: string, refreshToken: string) {
  // Access token expires in 1 day
  document.cookie = `saturn_access_token=${accessToken}; path=/; max-age=${ACCESS_TOKEN_MAX_AGE}; SameSite=Lax`;
  
  // Refresh token expires in 30 days
  document.cookie = `saturn_refresh_token=${refreshToken}; path=/; max-age=${REFRESH_TOKEN_MAX_AGE}; SameSite=Lax`;
  
  // Also store in localStorage for api-client.ts
  localStorage.setItem('saturn_access_token', accessToken);
  localStorage.setItem('saturn_refresh_token', refreshToken);
}

export function clearAuthCookies() {
  // Clear cookies
  document.cookie = 'saturn_access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'saturn_refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  
  // Clear localStorage
  localStorage.removeItem('saturn_access_token');
  localStorage.removeItem('saturn_refresh_token');
  localStorage.removeItem('user_profile_picture');
}

export function getAuthCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  
  return null;
}

// Parse JWT token to extract payload
export function parseJwt(token: string): any | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

// Check if JWT token is expired
export function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) {
    return true; // If we can't parse it or there's no expiry, consider it expired
  }
  
  // JWT exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  
  // Add 30 second buffer to logout slightly before actual expiry
  const bufferTime = 30 * 1000;
  
  return currentTime >= (expirationTime - bufferTime);
}

// Get access token and check if it's expired
export function getValidAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  
  // Try localStorage first, then cookies
  const token = localStorage.getItem('saturn_access_token') || getAuthCookie('saturn_access_token');
  
  if (!token) return null;
  
  // Check if token is expired
  if (isTokenExpired(token)) {
    console.warn('⚠️ Access token has expired');
    return null;
  }
  
  return token;
}

// Get refresh token
export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('saturn_refresh_token') || getAuthCookie('saturn_refresh_token');
}

// Check if the access token is close to expiring (within 5 minutes)
export function isTokenExpiringSoon(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) {
    return true;
  }
  
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const fiveMinutes = 5 * 60 * 1000;
  
  return currentTime >= (expirationTime - fiveMinutes);
}

/**
 * Proactively refresh the access token using the refresh token.
 * Returns true if refresh was successful, false otherwise.
 */
export async function refreshAccessToken(): Promise<boolean> {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const accessToken = localStorage.getItem('saturn_access_token') || getAuthCookie('saturn_access_token');
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    console.warn('⚠️ No refresh token available');
    return false;
  }

  isRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      console.log('🔄 Attempting to refresh access token...');
      
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
      const { data } = await axios.post(`${baseUrl}/v3/auth/refresh`, {
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      const newAccessToken = data.access_token;
      const newRefreshToken = data.refresh_token;

      if (newAccessToken && newRefreshToken) {
        // Update tokens in both cookies and localStorage
        setAuthCookies(newAccessToken, newRefreshToken);
        console.log('✅ Access token refreshed successfully');
        return true;
      }

      console.warn('⚠️ Refresh response missing tokens');
      return false;
    } catch (error: any) {
      console.error('❌ Token refresh failed:', error?.response?.data || error?.message);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

